import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Loader2, Save, Percent } from "lucide-react";
import { apiGet, apiPut } from "../../api";

// Rebate value types configuration
const REBATE_TYPES = {
  default: [
    { key: "REBATE_DEFAULT_SELF_PERCENTAGE", label: "Self Percentage" },
    { key: "REBATE_DEFAULT_REF_PERCENTAGE", label: "Referral Percentage" },
  ],
  market: {
    NU: [
      { key: "REBATE_MARKET_NU_LEVEL_1", label: "Level 1" },
      { key: "REBATE_MARKET_NU_LEVEL_2", label: "Level 2" },
      { key: "REBATE_MARKET_NU_LEVEL_3", label: "Level 3" },
    ],
    BD: [
      { key: "REBATE_MARKET_BD_LEVEL_1", label: "Level 1" },
      { key: "REBATE_MARKET_BD_LEVEL_2", label: "Level 2" },
      { key: "REBATE_MARKET_BD_LEVEL_3", label: "Level 3" },
      { key: "REBATE_MARKET_BD_LEVEL_4", label: "Level 4" },
      { key: "REBATE_MARKET_BD_LEVEL_5", label: "Level 5" },
    ],
  },
  limit: {
    NU: [
      { key: "REBATE_LIMIT_NU_LEVEL_1", label: "Level 1" },
      { key: "REBATE_LIMIT_NU_LEVEL_2", label: "Level 2" },
      { key: "REBATE_LIMIT_NU_LEVEL_3", label: "Level 3" },
    ],
    BD: [
      { key: "REBATE_LIMIT_BD_LEVEL_1", label: "Level 1" },
      { key: "REBATE_LIMIT_BD_LEVEL_2", label: "Level 2" },
      { key: "REBATE_LIMIT_BD_LEVEL_3", label: "Level 3" },
      { key: "REBATE_LIMIT_BD_LEVEL_4", label: "Level 4" },
      { key: "REBATE_LIMIT_BD_LEVEL_5", label: "Level 5" },
    ],
  },
};

const getAllRebateKeys = () => {
  const keys = [];
  REBATE_TYPES.default.forEach((item) => keys.push(item.key));
  Object.values(REBATE_TYPES.market).forEach((levels) =>
    levels.forEach((item) => keys.push(item.key))
  );
  Object.values(REBATE_TYPES.limit).forEach((levels) =>
    levels.forEach((item) => keys.push(item.key))
  );
  return keys;
};

const RebateSettings = () => {
  const [activeTab, setActiveTab] = useState("default");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({});
  const [editedValues, setEditedValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchAllValues();
  }, []);

  const fetchAllValues = async () => {
    setLoading(true);
    const allKeys = getAllRebateKeys();
    const fetchedValues = {};
    const fetchedEditedValues = {};

    try {
      const promises = allKeys.map((key) =>
        apiGet(`/api/admin/general/values/${key}`)
          .then((res) => {
            if (res.data?.success) {
              fetchedValues[key] = {
                value: res.data.data?.value || "0",
                _id: res.data.data?._id,
                description: res.data.data?.description || "",
              };
              fetchedEditedValues[key] = res.data.data?.value || "0";
            }
          })
          .catch(() => {
            fetchedValues[key] = { value: "0", _id: null, description: "" };
            fetchedEditedValues[key] = "0";
          })
      );

      await Promise.all(promises);
      setValues(fetchedValues);
      setEditedValues(fetchedEditedValues);
    } catch (err) {
      toast.error("Failed to fetch rebate values");
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (key, value) => {
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    const newEditedValues = { ...editedValues, [key]: value };
    setEditedValues(newEditedValues);
    const changed = Object.keys(newEditedValues).some(
      (k) => newEditedValues[k] !== values[k]?.value
    );
    setHasChanges(changed);
  };

  const handleSave = async () => {
    setSaving(true);
    const changedKeys = Object.keys(editedValues).filter(
      (key) => editedValues[key] !== values[key]?.value
    );
    try {
      const promises = changedKeys.map((key) =>
        apiPut(`/api/admin/general/values/${values[key]?._id}`, {
          value: editedValues[key],
          description: values[key]?.description,
        })
      );
      await Promise.all(promises);
      toast.success("Rebate settings updated successfully!");
      const updatedValues = { ...values };
      changedKeys.forEach((key) => {
        updatedValues[key] = { ...updatedValues[key], value: editedValues[key] };
      });
      setValues(updatedValues);
      setHasChanges(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update values");
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: "default", label: "Default Percentages" },
    { id: "market", label: "Market Orders" },
    { id: "limit", label: "Limit Orders" },
  ];

  const labelCls = "block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

  const renderInput = (item) => (
    <div key={item.key} className="flex flex-col">
      <label className={labelCls}>{item.label}</label>
      <div className="relative">
        <input
          type="text"
          value={editedValues[item.key] || ""}
          onChange={(e) => handleValueChange(item.key, e.target.value)}
          className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 pr-8 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
          placeholder="0"
        />
        <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground/50" />
      </div>
    </div>
  );

  const renderDefaultTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {REBATE_TYPES.default.map(renderInput)}
    </div>
  );

  const renderOrdersTab = (type) => (
    <div className="space-y-5">
      {/* NU Section */}
      <div className="rounded-lg border border-border/40 bg-white/[0.02] p-4">
        <div className="flex items-center gap-2 mb-4">
          <span className="rounded px-2 py-0.5 text-xs font-medium bg-white/[0.06] text-muted-foreground border border-border/40">NU</span>
          <h3 className="text-xs font-semibold uppercase tracking-[1px] text-muted-foreground">Normal User Levels</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {REBATE_TYPES[type].NU.map(renderInput)}
        </div>
      </div>

      {/* BD Section */}
      <div className="rounded-lg border border-border/40 bg-white/[0.02] p-4">
        <div className="flex items-center gap-2 mb-4">
          <span className="rounded px-2 py-0.5 text-xs font-medium bg-ginox-green/10 text-ginox-green border border-ginox-green/20">BD</span>
          <h3 className="text-xs font-semibold uppercase tracking-[1px] text-muted-foreground">Business Developer Levels</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {REBATE_TYPES[type].BD.map(renderInput)}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div>
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          System
        </div>
        <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
          Rebate <span className="text-gradient-ginox">Settings</span>
        </h1>
        <div className="flex justify-center items-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-ginox-green" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
            System
          </div>
          <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
            Rebate <span className="text-gradient-ginox">Settings</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Configure rebate percentages for all user levels and order types
          </p>
        </div>
        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save Changes
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-border/40 mb-6">
        <nav className="flex gap-0 overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-ginox-green text-ginox-green"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border/70"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="border-t border-white/[0.08] pt-6">
        {activeTab === "default" && renderDefaultTab()}
        {activeTab === "market" && renderOrdersTab("market")}
        {activeTab === "limit" && renderOrdersTab("limit")}
      </div>
    </div>
  );
};

export default RebateSettings;
